import axios, { AxiosPromise, CancelToken } from 'axios';
import { ApPlan, CreateApPlanRequest, UpdateApPlanRequest } from '../sic-lib/types/ApPlan';
import {
    ApPlanAction,
    CreateApPlanActionRequest,
    CreateDirectApPlanActionRequest,
    UpdateApPlanActionRequest,
} from '../sic-lib/types/ApPlanAction';
import { apiInstance as instance } from './';

/**
 * Makes a HTTP request to retrieve an action plan based on its id
 * @param planId
 * @param cancelToken
 */
export function requestApPlanById(planId: number, cancelToken: CancelToken): AxiosPromise<ApPlan> {
    return instance.get(`/actionplans/${planId}`, {
        cancelToken: cancelToken,
    });
}

/**
 * Makes a HTTP request to create a new action plan
 * @param data
 * @param cancelToken
 */
export function requestCreateApPlan(
    data: CreateApPlanRequest,
    cancelToken: CancelToken,
): AxiosPromise<ApPlan> {
    return instance.post(`/actionplans`, data, {
        cancelToken: cancelToken,
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Makes a HTTP request to update an existing action plan matching the specified plan id
 * @param planId
 * @param data
 * @param cancelToken
 */
export function requestUpdateApPlan(
    planId: number,
    data: UpdateApPlanRequest,
    cancelToken: CancelToken,
): AxiosPromise<unknown> {
    return instance.put(`/actionplans/${planId}`, data, {
        cancelToken: cancelToken,
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Makes a HTTP request to delete an action plan matching the specified plan id
 * @param planId
 * @param cancelToken
 */
export function requestDeleteApPlan(
    planId: number,
    cancelToken: CancelToken,
): AxiosPromise<unknown> {
    return instance.delete(`/actionplans/${planId}`, {
        cancelToken: cancelToken,
    });
}

/**
 * Makes a HTTP request to retreive an action plan's action matching the given plan and action id
 * @param planId
 * @param actionId
 * @param cancelToken
 */
export function requestApPlanActionById(
    planId: number,
    actionId: number,
    cancelToken: CancelToken,
): AxiosPromise<ApPlanAction> {
    return instance.get(`/actionplans/${planId}/actions/${actionId}`, {
        cancelToken: cancelToken,
    });
}

/**
 * Makes a HTTP request to create a new action in an existing action plan that matches the given plan id
 * @param planId
 * @param data
 * @param cancelToken
 */
export function requestCreateApPlanAction(
    planId: number,
    data: CreateApPlanActionRequest,
    cancelToken: CancelToken,
): AxiosPromise<ApPlanAction> {
    return instance.post(`/actionplans/${planId}/actions`, data, {
        cancelToken: cancelToken,
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Makes a HTTP request to update an existing action plan's action matching the given plan and action id
 * @param planId
 * @param actionId
 * @param data
 * @param cancelToken
 */
export function requestUpdateApPlanAction(
    planId: number,
    actionId: number,
    data: UpdateApPlanActionRequest,
    cancelToken: CancelToken,
): AxiosPromise<unknown> {
    return instance.put(`/actionplans/${planId}/actions/${actionId}`, data, {
        cancelToken: cancelToken,
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Makes a HTTP request to delete an action from an existing action plan matching the given plan and action id
 * @param planId
 * @param actionId
 * @param cancelToken
 */
export function requestDeleteApPlanAction(
    planId: number,
    actionId: number,
    cancelToken: CancelToken,
): AxiosPromise<unknown> {
    return instance.delete(`/actionplans/${planId}/actions/${actionId}`, {
        cancelToken: cancelToken,
    });
}

let requestSchedulePlan_cancel: any;
/**
 * Makes a HTTP request to schedule a single action plan
 * @param planId
 * @param cancelToken
 */
export function requestSchedulePlan(
    planId: number,
    cancelToken?: CancelToken,
): AxiosPromise<ApPlan> {
    if (requestSchedulePlan_cancel) requestSchedulePlan_cancel();

    return instance.post(
        `/actionplans/${planId}/schedule`,
        {},
        {
            cancelToken:
                cancelToken ||
                new axios.CancelToken(function executor(c) {
                    requestSchedulePlan_cancel = c;
                }),
        },
    );
}

let requestBatchSchedulePlans_cancel: any;
/**
 * Makes a HTTP request to schedule multiple aciton plans at once
 * @param planIds An array of action plan id's to be scheduled
 * @param cancelToken
 */
export function requestBatchSchedulePlans(
    data: { planIds: number[] },
    //cancelToken: CancelToken
): AxiosPromise<ApPlan[]> {
    if (requestBatchSchedulePlans_cancel) requestBatchSchedulePlans_cancel();

    return instance.post(`/actionplans/schedulebatch`, data, {
        //cancelToken: cancelToken,
        cancelToken: new axios.CancelToken(function executor(c) {
            requestBatchSchedulePlans_cancel = c;
        }),
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

/**
 * Makes a HTTP request to create an aciton plan with a pre-populated action
 * @param data
 * @param cancelToken
 */
export function requestCreateDirectApPlan(
    data: CreateDirectApPlanActionRequest,
    cancelToken: CancelToken,
): AxiosPromise<ApPlan> {
    return instance.post(`/actionplans/createdirectaction`, data, {
        cancelToken: cancelToken,
        headers: {
            'Content-Type': 'application/json',
        },
    });
}
